<script setup lang="ts">
    const {
        variant = 'primary',
        icon = 'lucide:circle-arrow-right',
        size = 'md'
    } = defineProps<{
        variant?: 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'white' | 'black';
        icon?: string;
        size?: 'sm' | 'md' | 'lg' | 'xl';
    }>();

    defineOptions({
        inheritAttrs: false
    });

    const attrs = useAttrs();

    const isLink = computed(() => attrs.to != null || attrs.href != null);
    const iconSize = computed(() => {
        switch (size) {
            case 'sm':
                return '18px';
            case 'md':
                return '24px';
            case 'lg':
                return '32px';
            case 'xl':
                return '40px';
            default:
                return '24px';
        }
    });
</script>

<template>
    <NuxtLink
        v-if="isLink"
        v-bind="attrs"
        :class="['icon-button', variant]"
    >
        <Icon
            :name="icon"
            :size="iconSize"
        />
    </NuxtLink>
    <button
        v-else
        v-bind="attrs"
        :class="['icon-button', variant]"
    >
        <Icon
            :name="icon"
            :size="iconSize"
        />
    </button>
</template>

<style scoped lang="scss">
    .icon-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: calc(v-bind(iconSize) + 8px);
        height: calc(v-bind(iconSize) + 8px);
        border: none;
        line-height: 1;
        border-radius: var(--border-radius-circle);
        color: inherit;
        transition: background-color 0.2s ease;

        &:disabled {
            cursor: not-allowed;
        }

        &.primary {
            --icon-btn-brand: var(--brand-primary);
        }

        &.secondary {
            --icon-btn-brand: var(--brand-secondary);
        }

        &.tertiary {
            --icon-btn-brand: var(--brand-tertiary);
        }

        &.primary,
        &.secondary,
        &.tertiary {
            background-color: var(--icon-btn-brand);
            color: var(--white);

            @media (hover: hover) {
                &:hover:not(:disabled) {
                    background-color: color-mix(in srgb, var(--icon-btn-brand) 30%, white 70%);
                }
            }

            &:focus-visible:not(:disabled) {
                outline: none;
                box-shadow: inset 0 0 0 4px var(--color-focus);
                background-color: color-mix(in srgb, var(--icon-btn-brand) 30%, white 70%);
            }

            &:active:not(:disabled) {
                background-color: color-mix(in srgb, var(--icon-btn-brand) 70%, white 30%);
            }

            &:disabled {
                background-color: rgb(from var(--icon-btn-brand) r g b / 0.5);
            }
        }

        &.ghost {
            background-color: transparent;
            color: var(--button-color);

            @media (hover: hover) {
                &:hover:not(:disabled) {
                    background-color: var(--ghost-hover-color);
                }
            }

            &:focus-visible:not(:disabled) {
                outline: none;
                box-shadow: inset 0 0 0 4px var(--color-focus);
                background-color: var(--ghost-hover-color);
            }

            &:active:not(:disabled) {
                background-color: color-mix(in srgb, var(--ghost-hover-color) 85%, black 15%);
            }

            &:disabled {
                color: var(--button-disabled-color);
            }
        }

        &.white {
            background-color: var(--white);
            color: var(--button-color);

            @media (hover: hover) {
                &:hover:not(:disabled) {
                    background-color: color-mix(in srgb, var(--white) 30%, transparent 70%);
                }
            }

            &:focus-visible:not(:disabled) {
                outline: none;
                box-shadow: inset 0 0 0 4px var(--color-focus);
                background-color: color-mix(in srgb, var(--white) 30%, transparent 70%);
            }

            &:active:not(:disabled) {
                background-color: color-mix(in srgb, var(--white) 70%, transparent 30%);
            }

            &:disabled {
                background-color: rgb(from var(--white) r g b / 0.5);
            }
        }

        &.black {
            background-color: var(--black);
            color: var(--white);

            @media (hover: hover) {
                &:hover:not(:disabled) {
                    background-color: color-mix(in srgb, var(--black) 30%, transparent 70%);
                }
            }

            &:focus-visible:not(:disabled) {
                outline: none;
                box-shadow: inset 0 0 0 4px var(--color-focus);
                background-color: color-mix(in srgb, var(--black) 30%, transparent 70%);
            }

            &:active:not(:disabled) {
                background-color: color-mix(in srgb, var(--black) 70%, transparent 30%);
            }

            &:disabled {
                background-color: rgb(from var(--black) r g b / 0.5);
            }
        }

        :deep(span.iconify) {
            color: inherit;
        }
    }
</style>
